import type { ReactNode } from "react";
import { Space, Tag, Tooltip, Typography } from "antd";
import type { FiltersDisplay } from "../types/filters";

const { Text } = Typography;

// Renders a filter group as chips, truncating long lists (e.g. 76 CPV codes) to `max` visible
// with a "…i N więcej" chip whose tooltip carries the full list.
export function TagGroup({
  keyPrefix,
  color,
  values,
  max = 6,
  format = (v) => v,
}: {
  keyPrefix: string;
  color: string;
  values: string[];
  max?: number;
  format?: (value: string) => string;
}): ReactNode[] {
  if (values.length === 0) return [];
  const visible = values.slice(0, max);
  const hidden = values.slice(max);
  const chips: ReactNode[] = visible.map((value, index) => (
    <Tag key={`${keyPrefix}-${index}`} color={color}>
      {format(value)}
    </Tag>
  ));
  if (hidden.length > 0) {
    chips.push(
      <Tooltip key={`${keyPrefix}-more`} title={hidden.map(format).join("\n")}>
        <Tag color={color}>…i {hidden.length} więcej</Tag>
      </Tooltip>,
    );
  }
  return chips;
}

function rangeLabel(
  prefix: string,
  min: string | number | null,
  max: string | number | null,
): string | null {
  if (min == null && max == null) return null;
  const from = min != null ? String(min) : "…";
  const to = max != null ? String(max) : "…";
  return `${prefix}: ${from} – ${to}`;
}

/**
 * Concrete filter values as chips. Shared by the searches list (decoded from
 * `params_jsonb`) and the filter-copy preview (decoded from `UserFilters.filter`) — both
 * arrive in the same `FiltersDisplay` shape, so they must look the same.
 *
 * `full` skips truncation for detail views. Empty/legacy docs render a muted placeholder.
 */
export function FiltersCell({
  filters,
  full = false,
}: {
  filters: FiltersDisplay | null | undefined;
  full?: boolean;
}) {
  if (!filters) return <Text type="secondary">—</Text>;
  const cap = full ? 999 : undefined;
  const chips: ReactNode[] = [];
  chips.push(...TagGroup({ keyPrefix: "excluded", color: "red", values: filters.excluded_phrases ?? [],
    max: cap ?? 4, format: (v) => `Wyklucz: „${v}”` }));

  chips.push(
    ...TagGroup({
      keyPrefix: "phrase",
      color: "magenta",
      values: filters.phrases,
      max: cap ?? 4,
      format: (v) => `„${v}”`,
    }),
  );
  chips.push(
    ...TagGroup({
      keyPrefix: "ind",
      color: "geekblue",
      values: filters.industries.map((i) => i.name),
      max: cap ?? 6,
    }),
  );
  chips.push(
    ...TagGroup({
      keyPrefix: "cpv",
      color: "purple",
      values: filters.cpv_code,
      max: cap ?? 6,
      format: (v) => `CPV ${v}`,
    }),
  );
  chips.push(...TagGroup({ keyPrefix: "cat", color: "cyan", values: filters.categories, max: cap ?? 4 }));
  chips.push(...TagGroup({ keyPrefix: "form", color: "blue", values: filters.forms, max: cap ?? 4 }));
  chips.push(...TagGroup({ keyPrefix: "branch", color: "geekblue", values: filters.branches, max: cap ?? 4 }));
  chips.push(...TagGroup({ keyPrefix: "state", color: "green", values: filters.states, max: cap ?? 6 }));
  chips.push(...TagGroup({ keyPrefix: "district", color: "green", values: filters.districts, max: cap ?? 6 }));
  chips.push(...TagGroup({ keyPrefix: "city", color: "green", values: filters.cities, max: cap ?? 6 }));
  chips.push(...TagGroup({ keyPrefix: "country", color: "gold", values: filters.countries, max: cap ?? 4 }));
  chips.push(...TagGroup({ keyPrefix: "org", color: "volcano", values: filters.organizers, max: cap ?? 3 }));
  chips.push(...TagGroup({ keyPrefix: "src", color: "default", values: filters.sources, max: cap ?? 4 }));

  const budget = rangeLabel("Budżet", filters.budget_min, filters.budget_max);
  if (budget)
    chips.push(
      <Tag key="budget" color="orange">
        {budget}
      </Tag>,
    );
  const added = rangeLabel("Dodano", filters.created_at_min, filters.created_at_max);
  if (added)
    chips.push(
      <Tag key="added" color="default">
        {added}
      </Tag>,
    );
  const submitting = rangeLabel("Składanie", filters.submitting_date_min, filters.submitting_date_max);
  if (submitting)
    chips.push(
      <Tag key="submitting" color="default">
        {submitting}
      </Tag>,
    );

  if (chips.length === 0) return <Text type="secondary">bez filtrów</Text>;
  return (
    <Space size={[4, 4]} wrap>
      {chips}
    </Space>
  );
}
